/**
 * Parser for bulk option imports (CSV, TSV, Pipe-delimited, and Plain-text lines).
 *
 * Designed to bridge technical users (copying from spreadsheets, CSVs, or databases)
 * and non-technical coordinators (copying a list of songs or readings from Word or email).
 */

export interface ParsedOption {
  key: string;
  label: string;
  source_ref?: string | undefined;
  isAutoKey: boolean;
  isValid: boolean;
  validationError?: string | undefined;
}

export interface ParseResult {
  options: ParsedOption[];
  warnings: string[];
  delimiterDetected: 'tsv' | 'csv' | 'pipe' | 'semicolon' | 'lines';
  hasHeader: boolean;
}

/**
 * Normalizes text into a clean, URL-safe snake_case identifier suitable for a TemplateKey.
 * Accents and diacritics are decomposed and stripped (e.g. 'Génesis' -> 'genesis').
 */
export function slugifyKey(input: string): string {
  if (!input) return '';

  return input
    .normalize('NFD') // decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // remove accent marks
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // remove non-alphanumeric except hyphen and underscore
    .replace(/[\s-]+/g, '_') // replace whitespace and hyphens with single underscore
    .replace(/^_+|_+$/g, ''); // trim leading/trailing underscores
}

/**
 * Parses raw input text (pasted or uploaded) into structured options.
 */
export function parseOptionImport(
  rawText: string,
  existingKeys: Set<string> = new Set(),
): ParseResult {
  const result: ParseResult = {
    options: [],
    warnings: [],
    delimiterDetected: 'lines',
    hasHeader: false,
  };

  if (!rawText || rawText.trim() === '') {
    return result;
  }

  // Normalize line endings
  const lines = rawText
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return result;

  // Delimiter sniffing across the lines
  let tabCount = 0;
  let commaCount = 0;
  let pipeCount = 0;
  let semicolonCount = 0;

  for (const line of lines) {
    if (line.includes('\t')) tabCount++;
    if (line.includes('|')) pipeCount++;
    if (line.includes(';')) semicolonCount++;
    if (line.includes(',')) commaCount++;
  }

  let delimiter: string | null = null;
  if (tabCount >= lines.length * 0.5) {
    delimiter = '\t';
    result.delimiterDetected = 'tsv';
  } else if (pipeCount >= lines.length * 0.5) {
    delimiter = '|';
    result.delimiterDetected = 'pipe';
  } else if (semicolonCount >= lines.length * 0.5) {
    delimiter = ';';
    result.delimiterDetected = 'semicolon';
  } else if (commaCount >= lines.length * 0.5) {
    delimiter = ',';
    result.delimiterDetected = 'csv';
  } else {
    result.delimiterDetected = 'lines';
  }

  // Parse lines into tokens
  const rows: string[][] = [];
  for (const line of lines) {
    if (delimiter) {
      if (delimiter === ',') {
        rows.push(parseCsvLine(line));
      } else {
        rows.push(line.split(delimiter).map((col) => col.trim()));
      }
    } else {
      rows.push([line]);
    }
  }

  if (rows.length === 0) return result;

  // Header detection: check if first line looks like header labels
  const firstRow = rows[0];
  let looksLikeHeader = false;

  if (firstRow && firstRow.length > 0) {
    const firstRowLower = firstRow.map((c) => c.toLowerCase());
    const headerKeywords = [
      'key',
      'clave',
      'label',
      'etiqueta',
      'title',
      'titulo',
      'name',
      'nombre',
      'ref',
      'source_ref',
      'citation',
      'cita',
    ];
    looksLikeHeader =
      firstRowLower.some((c) => headerKeywords.includes(c)) ||
      (firstRowLower[0] === 'key' &&
        (firstRowLower[1] === 'label' || firstRowLower[1] === 'value'));
  }

  let startIndex = 0;
  if (looksLikeHeader) {
    result.hasHeader = true;
    startIndex = 1;
  }

  const assignedKeys = new Set<string>(existingKeys);

  for (let i = startIndex; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length === 0 || (row.length === 1 && !row[0])) continue;

    let key = '';
    let label = '';
    let sourceRef: string | undefined;
    let isAutoKey = false;

    if (row.length === 1) {
      // Single column: plain text label, auto-generate key
      label = row[0] ?? '';
      key = slugifyKey(label);
      isAutoKey = true;
    } else if (row.length === 2) {
      // Two columns: usually [key, label] or [label, key]
      const first = row[0] ?? '';
      const second = row[1] ?? '';

      if (isKeyFormat(first) && !isKeyFormat(second)) {
        key = slugifyKey(first);
        label = second;
      } else if (!isKeyFormat(first) && isKeyFormat(second)) {
        label = first;
        key = slugifyKey(second);
      } else {
        // Default assumption: col 0 is key, col 1 is label
        key = slugifyKey(first);
        label = second;
      }
    } else {
      // 3 or more columns: [key, label, source_ref, ...]
      const first = row[0] ?? '';
      const second = row[1] ?? '';
      const third = row[2];
      key = slugifyKey(first);
      label = second;
      sourceRef = third ? third.trim() : undefined;
    }

    if (!label || label.trim() === '') {
      label = key || `Option ${i + 1}`;
    }

    if (!key || key.trim() === '') {
      key = slugifyKey(label) || `option_${i + 1}`;
      isAutoKey = true;
    }

    // Ensure key uniqueness across this import batch and existing items
    let uniqueKey = key;
    let suffix = 2;
    while (assignedKeys.has(uniqueKey)) {
      uniqueKey = `${key}_${suffix}`;
      suffix++;
    }
    assignedKeys.add(uniqueKey);

    let isValid = true;
    let validationError: string | undefined;

    if (!uniqueKey || uniqueKey.trim() === '') {
      isValid = false;
      validationError = 'Invalid key identifier';
    } else if (!label || label.trim() === '') {
      isValid = false;
      validationError = 'Label cannot be empty';
    }

    result.options.push({
      key: uniqueKey,
      label,
      source_ref: sourceRef,
      isAutoKey,
      isValid,
      validationError,
    });
  }

  return result;
}

/**
 * Basic CSV line tokenizer that respects double quotes.
 */
function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++; // skip escaped quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function isKeyFormat(val: string): boolean {
  return /^[a-z0-9_]+$/.test(val.trim());
}

/**
 * Generates sample CSV content for download.
 */
export function generateOptionsCsvSample(): string {
  return [
    'key,label,source_ref',
    'genesis_1_26,"Genesis 1:26-28a (Male and female he created them)","Lectionary 801-1"',
    'tobit_8_4b,"Tobit 8:4b-8 (Allow us to live together to a happy old age)","Lectionary 801-3"',
    'psalm_34,"Psalm 34: I will bless the Lord at all times","Lectionary 802-2"',
    '1_corinthians_12_31,"1 Corinthians 12:31-13:8a (If I do not have love, I gain nothing)","Lectionary 803-3"',
  ].join('\n');
}
